import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, Plus, RefreshCw, Store, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { PhoneInput } from '../../ui/PhoneInput';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { usePermission } from '../../../hooks/useAuth';
import { useCdnAgents, useCreateAdminAgentShop } from '../../../hooks/useApi';
import { apiClient } from '../../../services/api';
import { PageHeader } from '@kioskgaming/ui';
import {
  costAsPercent,
  isChildCostRateValid,
  normalizeCostRate,
  percentInputToRate,
} from '../agents/agentCdnUtils';
import type { CdnAgent, CdnShop } from '../../../types';
import { getCdnShopUsernameError } from '../../../utils/username';

type ShopRow = CdnShop & {
  agentId: string;
  agentName: string;
};

const PASSWORD_MIN = 8;

const ShopManagement: React.FC = () => {
  const canManage = usePermission('agentManagement');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'terminated'>('all');

  const { data: agentsResponse } = useCdnAgents(canManage);
  const createShop = useCreateAdminAgentShop();
  const [createOpen, setCreateOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [createCostError, setCreateCostError] = useState<string | null>(null);
  const [form, setForm] = useState({
    agent_id: '',
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    cost_percent: '0',
  });

  const agents = useMemo(() => agentsResponse?.data ?? [], [agentsResponse?.data]);
  const activeAgents = useMemo(() => agents.filter((a) => a.status === 'active'), [agents]);
  const selectedAgentForCreate = useMemo(
    () => activeAgents.find((a) => a.id === form.agent_id),
    [activeAgents, form.agent_id],
  );

  const createCostPercentValue = parseFloat(form.cost_percent);
  const createCostPercentOk =
    Number.isFinite(createCostPercentValue) &&
    createCostPercentValue >= 0 &&
    createCostPercentValue < 100;
  const proposedShopCostRate = percentInputToRate(form.cost_percent);
  const parentAgentCostRate = normalizeCostRate(selectedAgentForCreate?.costRate);
  const createCostHierarchyOk =
    !form.agent_id.trim() ||
    (!!selectedAgentForCreate && isChildCostRateValid(parentAgentCostRate, proposedShopCostRate));

  const resetCreateForm = () => {
    setForm({
      agent_id: '',
      name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      cost_percent: '0',
    });
    setShowPassword(false);
    setCreateCostError(null);
  };

  const handleCreateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;
    if (!form.agent_id.trim()) return;
    if (!selectedAgentForCreate) return;
    if (form.password.length < PASSWORD_MIN) return;
    if (!createCostPercentOk) {
      setCreateCostError('Cost rate must be at least 0% and less than 100%.');
      return;
    }
    if (!isChildCostRateValid(parentAgentCostRate, proposedShopCostRate)) {
      setCreateCostError(
        `Shop cost rate must be at least agent cost (${(parentAgentCostRate * 100).toFixed(2)}%).`,
      );
      return;
    }
    const usernameError = getCdnShopUsernameError(form.username);
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    const username = form.username.trim();
    setCreateCostError(null);
    try {
      await createShop.mutateAsync({
        agentId: form.agent_id.trim(),
        data: {
          name: form.name.trim(),
          ...(username ? { username } : {}),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          password: form.password,
          cost_rate: percentInputToRate(form.cost_percent),
        },
      });
      resetCreateForm();
      setCreateOpen(false);
    } catch {
      // Toast from useCreateAdminAgentShop
    }
  };

  const {
    data: shops,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<ShopRow[], Error>(
    ['adminShops'],
    async () => {
      const listResponse = await apiClient.getCdnAgents();
      const agentList = listResponse?.data ?? [];

      const shopGroups = await Promise.all(
        agentList.map(async (agent: CdnAgent) => {
          const detailResponse = await apiClient.getAdminAgentDetail(agent.id);
          const agentShops = detailResponse?.data?.shops ?? [];
          return agentShops.map((shop: CdnShop) => ({
            ...shop,
            agentId: agent.id,
            agentName: agent.name,
          }));
        }),
      );

      return shopGroups.flat();
    },
    {
      enabled: canManage,
      staleTime: 30 * 1000,
    },
  );

  const filteredShops = useMemo(() => {
    const rows = shops ?? [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return rows.filter((shop) => {
      const statusMatched = statusFilter === 'all' ? true : shop.status === statusFilter;
      const searchMatched = normalizedSearch
        ? `${shop.name} ${shop.email} ${shop.agentName}`.toLowerCase().includes(normalizedSearch)
        : true;
      return statusMatched && searchMatched;
    });
  }, [shops, searchTerm, statusFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shop Management"
        description="Browse and create shops under agents"
        actions={
          <div className="flex flex-wrap gap-2">
            {canManage ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create shop
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        }
      />

      {canManage && createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createShop.isLoading) {
              setCreateOpen(false);
              resetCreateForm();
            }
          }}
        >
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                New shop
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreateOpen(false);
                  resetCreateForm();
                }}
                disabled={createShop.isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateShop} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="create-shop-agent" className="text-sm font-medium">
                    Agent
                  </label>
                  <select
                    id="create-shop-agent"
                    required
                    value={form.agent_id}
                    onChange={(ev) => {
                      setCreateCostError(null);
                      setForm((f) => ({ ...f, agent_id: ev.target.value }));
                    }}
                    disabled={createShop.isLoading}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select agent</option>
                    {activeAgents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.email})
                      </option>
                    ))}
                  </select>
                  {activeAgents.length === 0 ? (
                    <p className="text-xs text-destructive">
                      No active agents. Create an agent under a super agent first.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Required — shop is created under this agent.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Shop name</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                    disabled={createShop.isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="global-create-shop-username">
                    User name
                  </label>
                  <Input
                    id="global-create-shop-username"
                    autoComplete="username"
                    placeholder="Login username (optional)"
                    value={form.username}
                    onChange={(ev) => setForm((f) => ({ ...f, username: ev.target.value }))}
                    disabled={createShop.isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. 3–80 chars: letters, numbers, dot, underscore, dash.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(ev) => setForm((f) => ({ ...f, email: ev.target.value }))}
                    disabled={createShop.isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="global-create-shop-phone">
                    Phone (optional)
                  </label>
                  <PhoneInput
                    id="global-create-shop-phone"
                    value={form.phone}
                    onChange={(phone) => setForm((f) => ({ ...f, phone }))}
                    disabled={createShop.isLoading}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <div className="relative">
                    <Input
                      required
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(ev) => setForm((f) => ({ ...f, password: ev.target.value }))}
                      className="pr-10"
                      disabled={createShop.isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cost rate (%)</label>
                  {selectedAgentForCreate ? (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Agent cost rate (reference): </span>
                      <span className="font-semibold text-foreground">{costAsPercent(selectedAgentForCreate)}</span>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
                        Shop cost must be <span className="font-medium text-foreground">at least</span> this value
                        (hierarchy rule) and <span className="font-medium text-foreground">under 100%</span>.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Select an agent to see its cost rate.</p>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={99.99}
                        value={form.cost_percent}
                        onChange={(ev) => {
                          setCreateCostError(null);
                          setForm((f) => ({ ...f, cost_percent: ev.target.value }));
                        }}
                        className="w-full"
                        disabled={createShop.isLoading}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground shrink-0 font-medium">%</span>
                  </div>
                  {createCostError ? (
                    <p className="text-xs text-destructive" role="alert">
                      {createCostError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Enter a percentage between 0 and under 100 (e.g. 92.5 for 92.5%).
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateOpen(false);
                      resetCreateForm();
                    }}
                    disabled={createShop.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createShop.isLoading ||
                      form.password.length < PASSWORD_MIN ||
                      !form.agent_id.trim() ||
                      activeAgents.length === 0 ||
                      !createCostPercentOk ||
                      !createCostHierarchyOk
                    }
                  >
                    {createShop.isLoading ? 'Creating…' : 'Create shop'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="shop-search" className="text-sm font-medium">
                Search
              </label>
              <Input
                id="shop-search"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Shop, email, or agent name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="shop-status-filter" className="text-sm font-medium">
                Status
              </label>
              <select
                id="shop-status-filter"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | 'active' | 'suspended' | 'terminated')
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Store className="h-5 w-5 text-muted-foreground" />
            Shops list
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">Failed to load shops.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shop</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Cost (%)</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredShops.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No shops found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredShops.map((shop) => (
                      <TableRow key={shop.id}>
                        <TableCell>
                          <Link
                            to={`/shops/${shop.id}`}
                            className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {shop.name}
                          </Link>
                        </TableCell>
                        <TableCell>{shop.email}</TableCell>
                        <TableCell>
                          <Link
                            to={`/agents/${shop.agentId}`}
                            className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {shop.agentName}
                          </Link>
                        </TableCell>
                        <TableCell>{costAsPercent(shop)}</TableCell>
                        <TableCell>
                          <span
                            className={
                              shop.status === 'active'
                                ? 'inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium uppercase tracking-wide'
                                : shop.status === 'suspended'
                                  ? 'inline-flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium uppercase tracking-wide'
                                  : 'inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium uppercase tracking-wide'
                            }
                          >
                            {shop.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <div className="px-6 py-3 text-sm text-muted-foreground border-t">
          Total: <span className="font-medium text-foreground">{filteredShops.length}</span>
          {isFetching && !isLoading && <span className="ml-2">(Refreshing...)</span>}
        </div>
      </Card>
    </div>
  );
};

export default ShopManagement;
